/**
 * Shared constants for config - SINGLE SOURCE OF TRUTH
 * All packages (server, frontend) import from here
 */

// ============================================================================
// Day of Week Constants (0 = Sunday, 6 = Saturday)
// ============================================================================

export const SUNDAY = 0;
export const MONDAY = 1;
export const TUESDAY = 2;
export const WEDNESDAY = 3;
export const THURSDAY = 4;
export const FRIDAY = 5;
export const SATURDAY = 6;

export const ALL_DAYS_OF_WEEK = [
	SUNDAY,
	MONDAY,
	TUESDAY,
	WEDNESDAY,
	THURSDAY,
	FRIDAY,
	SATURDAY,
] as const satisfies readonly number[];

/**
 * Default working days (Monday through Friday)
 * Standard international business week
 */
export const DEFAULT_WORKING_DAYS = [
	MONDAY,
	TUESDAY,
	WEDNESDAY,
	THURSDAY,
	FRIDAY,
] as const satisfies readonly number[];

// ============================================================================
// Time Constants
// ============================================================================

export const DEFAULT_START_TIME = "09:00";
export const DEFAULT_END_TIME = "17:00";
export const DEFAULT_CUSTOM_RANGE_START_TIME = "09:00";
export const DEFAULT_CUSTOM_RANGE_END_TIME = "17:00";

// ============================================================================
// Duration & Capacity Constants
// ============================================================================

export const DEFAULT_SLOT_DURATION_HOURS = 2;
export const DEFAULT_SLOT_DURATION_MINUTES = DEFAULT_SLOT_DURATION_HOURS * 60;
export const MIN_SLOT_DURATION_MINUTES = 15;

export const DEFAULT_EVENT_DURATION_MINUTES = 30;
export const MIN_EVENT_DURATION_MINUTES = 5;
export const MAX_EVENT_DURATION_MINUTES = 480;
export const DEFAULT_FOLLOW_UP_MINUTES = 30;

// ============================================================================
// Calendar View Options
// ============================================================================

export interface CalendarViewOption {
	description?: string;
	label: string;
	value: string;
}

export const CALENDAR_VIEW_OPTIONS = [
	{
		value: "timeGridWeek",
		label: "Week (Time Grid)",
		description:
			"Week view with hourly grid slots (FullCalendar timeGridWeek).",
	},
	{
		value: "timeGridDay",
		label: "Day",
		description:
			"Single day view with hourly grid slots (FullCalendar timeGridDay).",
	},
	{
		value: "dayGridMonth",
		label: "Month",
		description: "Classic month grid with all-day tiles (dayGridMonth).",
	},
	{
		value: "listMonth",
		label: "List",
		description: "Agenda-style monthly list of events (listMonth).",
	},
	{
		value: "multiMonthYear",
		label: "Multi-Month",
		description: "Compact multi-month overview (multiMonthYear).",
	},
] as const satisfies readonly CalendarViewOption[];

/**
 * Default calendar views enabled by default.
 * Note: Day view (timeGridDay) is excluded from defaults - it must be explicitly enabled.
 * When day view is disabled, nav links (clicking on day numbers/titles) are also disabled.
 */
export const DEFAULT_CALENDAR_VIEW_VALUES = CALENDAR_VIEW_OPTIONS.filter(
	(o) => o.value !== "timeGridDay"
).map((o) => o.value) as unknown as readonly (
	| "dayGridMonth"
	| "listMonth"
	| "multiMonthYear"
	| "timeGridWeek"
)[];
export const DEFAULT_CALENDAR_VIEW = "timeGridWeek";

// ============================================================================
// Locale & Region Constants
// ============================================================================

export const DEFAULT_TIMEZONE = "UTC";
export const DEFAULT_COUNTRY_PREFIX = "US";
/**
 * Languages a new org OFFERS its members. Mirrors the server's
 * `config-defaults.ts` — both installed languages, because the home-dock
 * General tab hides the language control when there is only one option.
 */
export const DEFAULT_LANGUAGES = [
	"en",
	"ar",
] as const satisfies readonly string[];
export const DEFAULT_LOCALE = "en";

// ============================================================================
// Calendar Display Constants
// ============================================================================

export const DEFAULT_CALENDAR_FIRST_DAY = 0; // Sunday (international standard)
export const DEFAULT_CALENDAR_DIRECTION = "auto" as const;
export const DEFAULT_COLUMN_WIDTH = 100;
export const DEFAULT_CUSTOM_RANGE_NAME = "Custom Range";

// ============================================================================
// LLM Constants
// ============================================================================

export const DEFAULT_LLM_PROVIDER = "openrouter";
export const DEFAULT_LLM_REASONING_EFFORT = "medium" as const;
export const DEFAULT_LLM_VERBOSITY = "medium" as const;
export const DEFAULT_LLM_MAX_OUTPUT_TOKENS = 16_384;
export const DEFAULT_STREAM_REASONING = true;
// Per-agent, per-direction media gates (default on).
export const DEFAULT_AI_ASSISTANT_RECEIVE_IMAGES = true;
export const DEFAULT_AI_ASSISTANT_RECEIVE_AUDIO = true;
export const DEFAULT_AI_ASSISTANT_SEND_IMAGES = true;
export const DEFAULT_AI_ASSISTANT_SEND_AUDIO = true;
export const DEFAULT_SYSTEM_AGENT_RECEIVE_IMAGES = true;
export const DEFAULT_SYSTEM_AGENT_RECEIVE_AUDIO = true;
export const DEFAULT_SYSTEM_AGENT_SEND_IMAGES = true;
export const DEFAULT_SYSTEM_AGENT_SEND_AUDIO = true;

// ============================================================================
// System Prompts (Anonymized)
// ============================================================================

export const DEFAULT_SYSTEM_PROMPT =
	"Help users manage calendars, events, and communications with available tools.";

export const DEFAULT_SYSTEM_AGENT_PROMPT =
	"Perform internal batch operations and data queries with available tools.";

// ============================================================================
// Reminder Template Constants
// ============================================================================

/**
 * Available reminder variable keys that can be mapped to WhatsApp template parameters.
 * Used by the frontend template variable mapping UI.
 */
export type ReminderVariableKey =
	| "client_name"
	| "appointment_time"
	| "appointment_date"
	| "event_type"
	| "business_name"
	| "business_address";

export interface ReminderVariableOption {
	description: string;
	key: ReminderVariableKey;
	label: string;
}

export const REMINDER_VARIABLE_OPTIONS: readonly ReminderVariableOption[] = [
	{
		key: "client_name",
		label: "Client Name",
		description: "Name of the client (User.name)",
	},
	{
		key: "appointment_time",
		label: "Time",
		description: 'Formatted event time (e.g. "10:30 AM")',
	},
	{
		key: "appointment_date",
		label: "Date",
		description: 'Formatted event date (e.g. "February 17, 2026")',
	},
	{
		key: "event_type",
		label: "Event Type",
		description: "Resolved event type name from config",
	},
	{
		key: "business_name",
		label: "Business",
		description: "Business name from config",
	},
	{
		key: "business_address",
		label: "Address",
		description: "Business address from config",
	},
] as const;
